
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FileSpreadsheet, ArrowRightLeft, RefreshCw, Link } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface GoogleSheetsIntegrationSettingsProps {
  initialConnected?: boolean;
  onSaved: () => Promise<void>;
}

const GoogleSheetsIntegrationSettings: React.FC<GoogleSheetsIntegrationSettingsProps> = ({ 
  initialConnected = false,
  onSaved 
}) => {
  const { toast } = useToast();
  const [isConnected, setIsConnected] = useState(initialConnected);
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetId, setSheetId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastSynced, setLastSynced] = useState<string | null>(null);

  const handleConnect = async () => {
    try {
      setIsLoading(true);
      // Implement Google Sheets connection logic
      // This would typically be an API call to your backend
      
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      await onSaved();
      setIsConnected(true);
      setLastSynced(new Date().toLocaleString());
      
      toast({
        title: "Connection successful",
        description: "Your Google Sheets has been connected.",
      });
    } catch (error) {
      toast({
        title: "Connection failed",
        description: "Failed to connect to Google Sheets. Please check your credentials.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setIsLoading(true);
      // Implement Google Sheets sync logic
      
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      setLastSynced(new Date().toLocaleString());
      
      toast({
        title: "Sync completed",
        description: "Your Google Sheets data has been synchronized.",
      });
    } catch (error) {
      toast({
        title: "Sync failed",
        description: "Failed to sync with Google Sheets.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <FileSpreadsheet className="mr-2 h-6 w-6" />
          Google Sheets Integration
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Google Sheets Connection</Label>
            <Switch 
              checked={isConnected}
              onCheckedChange={setIsConnected}
            />
          </div>

          {isConnected ? (
            <div className="space-y-4">
              <div>
                <Label>Google Sheets URL</Label>
                <Input 
                  value={sheetUrl}
                  onChange={(e) => setSheetUrl(e.target.value)}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter the URL of your Google Sheet
                </p>
              </div>
              <div>
                <Label>Sheet ID</Label>
                <Input 
                  value={sheetId}
                  onChange={(e) => setSheetId(e.target.value)}
                  placeholder="Enter Sheet ID or Name"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  The name or ID of the specific sheet to use (e.g., "Sheet1")
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button 
                  onClick={handleConnect}
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? "Connecting..." : "Update Connection"}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleSync}
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Sync Now
                </Button>
              </div>
              
              <div className="bg-muted p-3 rounded-md">
                <h4 className="text-sm font-medium mb-2">Sheet Data</h4>
                <p className="text-sm">The following columns are mapped:</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="text-xs">Name → User Name</div>
                  <div className="text-xs">Email → User Email</div>
                  <div className="text-xs">Phone → User Phone</div>
                  <div className="text-xs">Referral Code → Referral Code</div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  {lastSynced ? `Last synced: ${lastSynced}` : 'Not synced yet'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Connect your Google Sheets to import and export user data.
              </p>
              <div className="bg-muted p-3 rounded-md">
                <h4 className="text-sm font-medium mb-2">Integration Steps</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="bg-primary/10 text-primary rounded-full h-5 w-5 flex items-center justify-center text-xs mr-2 mt-0.5 flex-shrink-0">1</span>
                    <span>Create a Google Sheet with columns for Name, Email, and Phone</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-primary/10 text-primary rounded-full h-5 w-5 flex items-center justify-center text-xs mr-2 mt-0.5 flex-shrink-0">2</span>
                    <span>Share your Google Sheet with editing permissions to <code className="bg-muted-foreground/20 px-1 py-0.5 rounded text-xs">service@yourapp.com</code></span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-primary/10 text-primary rounded-full h-5 w-5 flex items-center justify-center text-xs mr-2 mt-0.5 flex-shrink-0">3</span>
                    <span>Copy the URL of your Google Sheet and paste it here</span>
                  </li>
                </ul>
              </div>
              <Button onClick={handleConnect} className="w-full">
                Connect to Google Sheets
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default GoogleSheetsIntegrationSettings;
